import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CreateSignatureRequestPayload {
  patient_term_id: string;
  vet_name: string;
  vet_has_certificate: boolean;
  vet_email: string;
  vet_crmv?: string;
  vet_mapa?: string;
  responsible: { name: string; cpf: string; email: string };
  /** For query invalidation. */
  pet_id?: string;
}

export interface CreateSignatureRequestResult {
  request_id: string;
  sign_url: string | null;
  completed: boolean;
}

function lambdaBase(): string {
  const url = import.meta.env.VITE_PDF_LAMBDA_URL as string | undefined;
  if (!url) throw new Error("pdf_lambda_url_not_configured");
  return url.replace(/\/$/, "");
}

/** Vet-side: creates a signature request (and signs the vet part) via the PDF Lambda. */
export function useTermSignatureRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: CreateSignatureRequestPayload,
    ): Promise<CreateSignatureRequestResult> => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("unauthenticated");

      const resp = await fetch(`${lambdaBase()}/terms/signature-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          patient_term_id: payload.patient_term_id,
          vet_name: payload.vet_name,
          vet_has_certificate: payload.vet_has_certificate,
          vet_email: payload.vet_email,
          vet_crmv: payload.vet_crmv,
          vet_mapa: payload.vet_mapa,
          responsible: payload.responsible,
        }),
      });
      if (!resp.ok) {
        let code = `http_${resp.status}`;
        try {
          const json = await resp.json();
          if (json?.error) code = json.error as string;
        } catch {
          /* ignore */
        }
        throw new Error(code);
      }
      return (await resp.json()) as CreateSignatureRequestResult;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["patient-terms"] });
      if (variables.pet_id) {
        queryClient.invalidateQueries({ queryKey: ["patient-terms", variables.pet_id] });
      }
    },
  });
}
